import { gql, useLazyQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import {
  BaseText,
  BottomButtonsLayout,
  Button,
  FieldInput,
  Meta,
  theme,
} from 'aimo-ui';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { CAN_DELETE_CUSTOMER } from 'graphql/queries/canDeleteCustomer';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import useUpdateCustomerMutation from 'hooks/useUpdateCustomerMutation';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from 'stores/authStore';
import styled from 'styled-components';
import {
  ColumnWithMargins,
  SheetDetailsWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { Query, QueryCanDeleteCustomerArgs } from 'types/generatedSchemaTypes';
import { AccountNavProps } from 'types/navigation';
import { updateCache } from 'utils/cacheUtils';
import { isWeb } from 'utils/deviceUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const lockIcon = <Icon name="Locked-Outlined" color={theme.colors.gray300} />;

const InfoIcon = () => (
  <Icon name="Info-Filled" size="s" color={theme.colors.gray300} />
);

const StyledMeta = styled(Meta)`
  flex: 1;
`;

const blockAccountRemovalReasons = [
  'ongoingParking',
  'ongoingCharging',
  'failedPayments',
];

const ContactInfo: React.FC = () => {
  const { t } = useTranslation();
  const { customer } = useContext(AccountContext);
  const { goBack } = useNavigation<AccountNavProps>();
  const {
    flags: {
      account_contact_edit,
      account_contact_phone_number,
      account_contact_invoice_email,
    },
  } = useContext(FeatureFlagsContext);
  const allowContactEdit = showFeatureFlag(account_contact_edit);
  const tabBarHeight = useBottomMenuHeight();
  const showPhoneNumber = showFeatureFlag(account_contact_phone_number);
  const showInvoiceEmail = showFeatureFlag(account_contact_invoice_email);
  const [invoiceEmail, setInvoiceEmail] = useState(
    customer?.invoiceEmail || ''
  );
  const [contactEmail, setContactEmail] = useState(
    customer?.contactEmail || ''
  );
  const [phoneNumber, setPhoneNumber] = useState(customer?.mobilePhone || '');

  const [firstName, setFirstName] = useState(customer?.firstName || '');
  const [lastName, setLastName] = useState(customer?.lastName || '');
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { updateCustomer, loading: isUpdatingCustomer } =
    useUpdateCustomerMutation({ skipRefetch: true });

  const [fetchCanDeleteCustomer] = useLazyQuery<
    Query,
    QueryCanDeleteCustomerArgs
  >(CAN_DELETE_CUSTOMER, {
    fetchPolicy: 'no-cache',
    variables: { fetchEvPayments: true },
  });

  const handlePreventCustomerDeletion = (reasons: Record<string, unknown>) => {
    if (reasons.customerSubTypes) {
      useUiStore.getState().setSheetModal('accountRemovalErrorLinkedService');
    } else {
      useAuthStore.getState().setAccountRemoveWithWarnings(true);
      Object.keys(reasons).forEach((key) => {
        useAuthStore.getState().setAccountRemovalBlockers({
          ...useAuthStore.getState().accountRemovalBlockers,
          [key]: reasons[key],
        });
        if (blockAccountRemovalReasons.includes(key)) {
          useAuthStore.getState().setAccountRemoveWithWarnings(false);
        }
      });
      useUiStore.getState().setSheetModal('preventAccountRemoval');
    }
  };

  const handleUpdateDetails = () => {
    if (!editMode) {
      return setEditMode(true);
    }
    return updateCustomer({
      variables: {
        input: {
          firstName,
          lastName,
        },
      },
      onCompleted: () => {
        setEditMode(false);
        goBack();
      },
      update: (cache) => {
        updateCache(
          cache,
          { __typename: 'Customer', uid: customer?.uid },
          {
            fragment: gql`
              fragment CustomerFragment on Customer {
                firstName
                lastName
              }
            `,
            data: { firstName, lastName },
          }
        );
      },
    });
  };

  const handleRemove = async () => {
    setIsLoading(true);
    useAuthStore.getState().setAccountRemovalBlockers({});
    const { data: canDeleteCustomerData, error: canDeleteCustomerError } =
      await fetchCanDeleteCustomer();
    setIsLoading(false);

    if (
      canDeleteCustomerError?.graphQLErrors &&
      canDeleteCustomerError?.graphQLErrors?.length > 0
    ) {
      if (
        canDeleteCustomerError?.graphQLErrors?.find(Boolean)?.extensions
          ?.code === 'PREVENT_CUSTOMER_DELETION'
      ) {
        const preventCustomerDeletionReasons =
          canDeleteCustomerError?.graphQLErrors?.find(Boolean)?.extensions
            ?.data as Record<string, unknown>;
        handlePreventCustomerDeletion(preventCustomerDeletionReasons);
      } else {
        useUiStore.getState().setSheetModal('accountRemovalErrors');
      }
    } else if (canDeleteCustomerData) {
      goBack();
      useUiStore.getState().setSheetModal('removeAccount');
    }
  };

  return (
    <SheetDetailsWrapper pt={isWeb ? 120 : 20} pb={tabBarHeight}>
      <BottomButtonsLayout
        scrollable={true}
        contentBottom={
          <>
            <StyledWrapper mb={20}>
              <Button
                size="l"
                text={t('account.removeAccount')}
                {...testIdentifiers('contact-info-remove-button')}
                buttonType="transparent"
                highlightColor={theme.colors.aimoUiRed}
                onPress={handleRemove}
                loading={isLoading}
              />
            </StyledWrapper>
            <StyledWrapper mb={20}>
              <Button
                size="l"
                text={editMode ? t('button.save') : t('account.updateDetails')}
                testID="contact-info-update-button"
                onPress={handleUpdateDetails}
                loading={isUpdatingCustomer}
              />
            </StyledWrapper>
          </>
        }>
        <>
          {showInvoiceEmail && (
            <StyledWrapper mb={20}>
              <FieldInput
                disabled={!allowContactEdit}
                title={t('account.emailInvoice')}
                value={invoiceEmail}
                onChangeText={setInvoiceEmail}
                testID="contact-info-email-invoice"
                icon={!allowContactEdit && lockIcon}
              />
            </StyledWrapper>
          )}
          <>
            <StyledWrapper mb={20}>
              <FieldInput
                disabled={!allowContactEdit}
                title={t('account.emailContact')}
                value={contactEmail}
                multiline
                onChangeText={setContactEmail}
                {...testIdentifiers('contact-info-email-contact')}
                icon={!allowContactEdit && lockIcon}
              />
              {editMode && (
                <StyledMeta
                  color={theme.colors.gray300}
                  iconComponent={InfoIcon}>
                  {t('account.contactEmailEditInfo')}
                </StyledMeta>
              )}
            </StyledWrapper>
          </>
          {showPhoneNumber && (
            <StyledWrapper mb={20}>
              <FieldInput
                disabled={!allowContactEdit}
                title={t('account.mobilePhone')}
                value={phoneNumber}
                onChangeText={setPhoneNumber}
                testID="contact-info-phone-number"
                icon={!allowContactEdit && lockIcon}
              />
            </StyledWrapper>
          )}
          <StyledWrapper mb={20}>
            <FieldInput
              disabled={!editMode}
              title={t('account.firstName')}
              value={firstName}
              onChangeText={setFirstName}
              testID="contact-info-firstname"
            />
          </StyledWrapper>
          <StyledWrapper mb={20}>
            <FieldInput
              disabled={!editMode}
              title={t('account.lastName')}
              value={lastName}
              onChangeText={setLastName}
              testID="contact-info-lastname"
            />
          </StyledWrapper>
          {!allowContactEdit && (
            <ColumnWithMargins mt={20} ml={20} mb={20} mr={20}>
              <BaseText color={theme.colors.black}>
                {t('account.updateDetailsHelpText')}
              </BaseText>
            </ColumnWithMargins>
          )}
        </>
      </BottomButtonsLayout>
    </SheetDetailsWrapper>
  );
};

export default ContactInfo;
